/**
 * PharmaFlow API Service
 *
 * Este serviço lida com a comunicação com o PharmaFlow (microserviço independente).
 * O PharmaFlow é um módulo separado com seu próprio banco de dados.
 *
 * Endpoints disponíveis:
 * - /api/v1/pdv/products - Catálogo de produtos
 * - /api/v1/pdv/categories - Categorias de produtos
 * - /api/v1/pdv/stores - Lojas/Filiais
 * - /api/v1/pdv/customers - Clientes
 * - /api/v1/pdv/carts - Carrinhos de compras
 * - /api/v1/pdv/orders - Pedidos
 * - /api/v1/pdv/quotes - Orçamentos
 */

import axios from "axios";

// Create axios instance for PharmaFlow API (independent microservice)
// PharmaFlow roda na porta 3001 com seu próprio banco de dados
const pharmaApi = axios.create({
  baseURL: (process.env.REACT_APP_PHARMAFLOW_URL || "http://localhost:3001") + "/api/v1",
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add JWT token
pharmaApi.interceptors.request.use(
  (config) => {
    // Get token from localStorage (same as Whaticket)
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${JSON.parse(token)}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
pharmaApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle specific error cases
    if (error?.response?.status === 401) {
      // Token expired or invalid - could redirect to login
      console.warn("[PharmaAPI] Authentication error - token may be expired");
    }
    if (error?.response?.status === 503) {
      // Service unavailable - PharmaFlow may be down
      console.warn("[PharmaAPI] Service unavailable - PharmaFlow may be down");
    }
    return Promise.reject(error);
  }
);

// ============================================
// Products API
// ============================================

export const searchProducts = async (query, storeId, options = {}) => {
  const params = {
    search: query,
    storeId,
    limit: options.limit || 20,
    page: options.page || 1,
    ...options,
  };
  const { data } = await pharmaApi.get("/pdv/products", { params });
  // Return in expected format
  return {
    products: data.data || data,
    total: data.total || (data.data ? data.data.length : 0),
    hasMore: data.hasMore || false,
  };
};

export const getProduct = async (id, storeId) => {
  const { data } = await pharmaApi.get(`/pdv/products/${id}`, {
    params: { storeId },
  });
  return data;
};

export const getProductByBarcode = async (barcode, storeId) => {
  const { data } = await pharmaApi.get(`/pdv/products/barcode/${barcode}`, {
    params: { storeId },
  });
  return data;
};

export const getSameFormulaProducts = async (productId, storeId, options = {}) => {
  const { data } = await pharmaApi.get(`/pdv/products/${productId}/same-formula`, {
    params: { storeId, limit: options.limit || 10, ...options },
  });
  return data;
};

export const getRelatedProducts = async (productId, storeId, options = {}) => {
  const { data } = await pharmaApi.get(`/pdv/products/${productId}/related`, {
    params: { storeId, limit: options.limit || 10, ...options },
  });
  return data;
};

// ============================================
// Categories / Product Groups API
// ============================================

export const getCategories = async (storeId) => {
  const { data } = await pharmaApi.get("/pdv/categories", {
    params: { storeId },
  });
  // API returns { data: [...], total, page, ... } - extract just the array
  return data.data || data;
};

// ============================================
// Product Groups API (New - with full CRUD)
// ============================================

/**
 * List product groups (categories) with pagination
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.parentId - Filter by parent group
 * @param {number} options.level - Filter by level (0 = root)
 * @param {boolean} options.isActive - Filter by active status
 */
export const listProductGroups = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/groups", { params: options });
  return data;
};

/**
 * Get product groups as hierarchical tree
 */
export const getProductGroupTree = async () => {
  const { data } = await pharmaApi.get("/pdv/groups/tree");
  return data;
};

/**
 * Get single product group by ID
 */
export const getProductGroup = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/groups/${id}`);
  return data;
};

/**
 * Create a new product group
 * @param {Object} groupData
 * @param {string} groupData.name - Group name
 * @param {string} groupData.parentId - Optional parent group ID
 * @param {string} groupData.code - Optional code
 * @param {string} groupData.description - Optional description
 * @param {string} groupData.imageUrl - Optional image URL
 * @param {number} groupData.sortOrder - Optional sort order
 */
export const createProductGroup = async (groupData) => {
  const { data } = await pharmaApi.post("/pdv/groups", groupData);
  return data;
};

/**
 * Update a product group
 */
export const updateProductGroup = async (id, groupData) => {
  const { data } = await pharmaApi.put(`/pdv/groups/${id}`, groupData);
  return data;
};

/**
 * Delete a product group
 */
export const deleteProductGroup = async (id) => {
  const { data } = await pharmaApi.delete(`/pdv/groups/${id}`);
  return data;
};

/**
 * Batch import product groups
 */
export const batchImportProductGroups = async (groups) => {
  const { data } = await pharmaApi.post("/pdv/groups/batch", { groups });
  return data;
};

// ============================================
// States API (Brazilian states)
// ============================================

/**
 * List Brazilian states (static list from API)
 * @returns {Promise<Array<{code: string, name: string, region: string}>>}
 */
export const listStates = async () => {
  const { data } = await pharmaApi.get("/pdv/states");
  return data;
};

// ============================================
// Cities API
// ============================================

/**
 * List cities with pagination
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.state - Filter by state (UF)
 * @param {boolean} options.isActive - Filter by active status
 */
export const listCities = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/cities", { params: options });
  return data;
};

/**
 * Get single city by ID with neighborhoods
 */
export const getCity = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/cities/${id}`);
  return data;
};

/**
 * Create a new city
 * @param {Object} cityData
 * @param {string} cityData.name - City name
 * @param {string} cityData.state - State (UF)
 * @param {string} cityData.ibgeCode - Optional IBGE code
 * @param {number} cityData.deliveryFee - Optional default delivery fee
 * @param {number} cityData.deliveryTime - Optional delivery time in minutes
 */
export const createCity = async (cityData) => {
  const { data } = await pharmaApi.post("/pdv/cities", cityData);
  return data;
};

/**
 * Update a city
 */
export const updateCity = async (id, cityData) => {
  const { data } = await pharmaApi.put(`/pdv/cities/${id}`, cityData);
  return data;
};

/**
 * Delete a city
 */
export const deleteCity = async (id) => {
  const { data } = await pharmaApi.delete(`/pdv/cities/${id}`);
  return data;
};

/**
 * Batch import cities
 */
export const batchImportCities = async (cities) => {
  const { data } = await pharmaApi.post("/pdv/cities/batch", { cities });
  return data;
};

// ============================================
// Neighborhoods API
// ============================================

/**
 * List neighborhoods with pagination
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.cityId - Filter by city
 * @param {boolean} options.isActive - Filter by active status
 * @param {boolean} options.isDeliverable - Filter by delivery availability
 */
export const listNeighborhoods = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/neighborhoods", { params: options });
  return data;
};

/**
 * Get single neighborhood by ID
 */
export const getNeighborhood = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/neighborhoods/${id}`);
  return data;
};

/**
 * Create a new neighborhood
 * @param {Object} neighborhoodData
 * @param {string} neighborhoodData.cityId - City ID
 * @param {string} neighborhoodData.name - Neighborhood name
 * @param {number} neighborhoodData.deliveryFee - Optional delivery fee
 * @param {number} neighborhoodData.deliveryTime - Optional delivery time
 * @param {boolean} neighborhoodData.isDeliverable - Whether delivery is available
 */
export const createNeighborhood = async (neighborhoodData) => {
  const { data } = await pharmaApi.post("/pdv/neighborhoods", neighborhoodData);
  return data;
};

/**
 * Update a neighborhood
 */
export const updateNeighborhood = async (id, neighborhoodData) => {
  const { data } = await pharmaApi.put(`/pdv/neighborhoods/${id}`, neighborhoodData);
  return data;
};

/**
 * Delete a neighborhood
 */
export const deleteNeighborhood = async (id) => {
  const { data } = await pharmaApi.delete(`/pdv/neighborhoods/${id}`);
  return data;
};

/**
 * Batch import neighborhoods
 */
export const batchImportNeighborhoods = async (neighborhoods) => {
  const { data } = await pharmaApi.post("/pdv/neighborhoods/batch", { neighborhoods });
  return data;
};

// ============================================
// Stores API
// ============================================

export const getStores = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/stores", { params: options });
  return data;
};

export const getStore = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/stores/${id}`);
  return data;
};

export const createStore = async (storeData) => {
  const { data } = await pharmaApi.post("/pdv/stores", storeData);
  return data;
};

export const updateStore = async (id, storeData) => {
  const { data } = await pharmaApi.patch(`/pdv/stores/${id}`, storeData);
  return data;
};

export const deleteStore = async (id) => {
  const { data } = await pharmaApi.delete(`/pdv/stores/${id}`);
  return data;
};

// ============================================
// Customers API
// ============================================

export const getCustomers = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/customers", { params: options });
  return data;
};

export const getCustomer = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/customers/${id}`);
  return data;
};

export const getCustomerByWhatsapp = async (whatsapp) => {
  const { data } = await pharmaApi.get(`/pdv/customers/whatsapp/${whatsapp}`);
  // API returns null if not found
  if (!data) {
    throw new Error("Customer not found");
  }
  return data;
};

export const getCustomerByContactId = async (contactId) => {
  const { data } = await pharmaApi.get(`/pdv/customers/contact/${contactId}`);
  // API returns null if not found
  if (!data) {
    throw new Error("Customer not found");
  }
  return data;
};

export const createCustomer = async (customerData) => {
  const { data } = await pharmaApi.post("/pdv/customers", customerData);
  return data;
};

export const updateCustomer = async (id, customerData) => {
  const { data } = await pharmaApi.patch(`/pdv/customers/${id}`, customerData);
  return data;
};

export const findOrCreateCustomer = async (customerData) => {
  const { data } = await pharmaApi.post("/pdv/customers/find-or-create", customerData);
  return data;
};

export const linkWhaticketContact = async (customerId, contactId) => {
  const { data } = await pharmaApi.post(`/pdv/customers/${customerId}/link-contact`, {
    whaticketContactId: contactId,
  });
  return data;
};

// Customer Addresses
export const addCustomerAddress = async (customerId, addressData) => {
  const { data } = await pharmaApi.post(`/pdv/customers/${customerId}/addresses`, addressData);
  return data;
};

export const updateCustomerAddress = async (customerId, addressId, addressData) => {
  const { data } = await pharmaApi.patch(
    `/pdv/customers/${customerId}/addresses/${addressId}`,
    addressData
  );
  return data;
};

export const deleteCustomerAddress = async (customerId, addressId) => {
  await pharmaApi.delete(`/pdv/customers/${customerId}/addresses/${addressId}`);
};

export const setDefaultAddress = async (customerId, addressId) => {
  const { data } = await pharmaApi.post(
    `/pdv/customers/${customerId}/addresses/${addressId}/set-default`
  );
  return data;
};

// Customer Profile & Statistics
/**
 * Get customer statistics (total orders, total spent, average ticket, purchases by category)
 * @param {string} customerId - Customer ID
 * @returns {Promise<CustomerStatsDto>}
 */
export const getCustomerStats = async (customerId) => {
  const { data } = await pharmaApi.get(`/pdv/customers/${customerId}/stats`);
  return data;
};

/**
 * Get customer purchase history (paginated)
 * @param {string} customerId - Customer ID
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.categoryId - Filter by category
 * @param {string} options.startDate - Filter by start date (YYYY-MM-DD)
 * @param {string} options.endDate - Filter by end date (YYYY-MM-DD)
 * @returns {Promise<CustomerPurchaseHistoryDto>}
 */
export const getCustomerPurchaseHistory = async (customerId, options = {}) => {
  const { data } = await pharmaApi.get(`/pdv/customers/${customerId}/purchase-history`, {
    params: options,
  });
  return data;
};

/**
 * Get product suggestions for a customer based on purchase history
 * @param {string} customerId - Customer ID
 * @param {Object} options - Query options
 * @param {string} options.storeId - Store ID for stock filtering
 * @param {number} options.limit - Max suggestions to return (default: 10)
 * @returns {Promise<CustomerSuggestionsDto>}
 */
export const getCustomerSuggestions = async (customerId, options = {}) => {
  const { data } = await pharmaApi.get(`/pdv/customers/${customerId}/suggestions`, {
    params: options,
  });
  return data;
};

// ============================================
// Carts API
// ============================================

export const createCart = async (cartData) => {
  const { data } = await pharmaApi.post("/pdv/carts", cartData);
  return data;
};

export const getCart = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/carts/${id}`);
  return data;
};

export const getCartByTicket = async (ticketId) => {
  const { data } = await pharmaApi.get(`/pdv/carts/ticket/${ticketId}`);
  return data;
};

export const addCartItem = async (cartId, itemData) => {
  const { data } = await pharmaApi.post(`/pdv/carts/${cartId}/items`, itemData);
  return data;
};

export const updateCartItem = async (cartId, itemId, itemData) => {
  const { data } = await pharmaApi.patch(`/pdv/carts/${cartId}/items/${itemId}`, itemData);
  return data;
};

export const removeCartItem = async (cartId, itemId) => {
  const { data } = await pharmaApi.delete(`/pdv/carts/${cartId}/items/${itemId}`);
  return data;
};

export const clearCart = async (cartId) => {
  const { data } = await pharmaApi.delete(`/pdv/carts/${cartId}/items`);
  return data;
};

export const setDeliveryFee = async (cartId, deliveryFee) => {
  const { data } = await pharmaApi.patch(`/pdv/carts/${cartId}/delivery-fee`, { deliveryFee });
  return data;
};

// ============================================
// Quotes API
// ============================================

export const createQuote = async (quoteData) => {
  // Use /quotes/direct endpoint when items are provided directly (not from cart)
  const endpoint = quoteData.cartId ? "/pdv/quotes" : "/pdv/quotes/direct";
  const { data } = await pharmaApi.post(endpoint, quoteData);
  return data;
};

export const getQuote = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/quotes/${id}`);
  return data;
};

export const searchQuotes = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/quotes", { params: options });
  return data;
};

export const sendQuoteToCustomer = async (quoteId, ticketId, ticketUuid) => {
  const { data } = await pharmaApi.post(`/pdv/quotes/${quoteId}/send`, { ticketId, ticketUuid });
  return data;
};

export const cancelQuote = async (quoteId) => {
  const { data } = await pharmaApi.patch(`/pdv/quotes/${quoteId}/cancel`);
  return data;
};

export const convertQuoteToOrder = async (quoteId) => {
  const { data } = await pharmaApi.post(`/pdv/quotes/${quoteId}/convert`);
  return data;
};

// ============================================
// Orders API
// ============================================

export const createOrder = async (orderData) => {
  // Use /orders/direct endpoint when items are provided directly (not from cart)
  const endpoint = orderData.cartId ? "/pdv/orders" : "/pdv/orders/direct";
  const { data } = await pharmaApi.post(endpoint, orderData);
  return data;
};

export const getOrder = async (id) => {
  const { data } = await pharmaApi.get(`/pdv/orders/${id}`);
  return data;
};

export const searchOrders = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/orders", { params: options });
  return data;
};

export const updateOrderStatus = async (orderId, status) => {
  const { data } = await pharmaApi.patch(`/pdv/orders/${orderId}/status`, { status });
  return data;
};

export const cancelOrder = async (orderId) => {
  const { data } = await pharmaApi.patch(`/pdv/orders/${orderId}/cancel`);
  return data;
};

// ============================================
// Settings API
// ============================================

export const getSettings = async () => {
  const { data } = await pharmaApi.get("/pdv/settings");
  return data;
};

export const updateSettings = async (settingsData) => {
  const { data } = await pharmaApi.patch("/pdv/settings", settingsData);
  return data;
};

export const formatQuoteMessage = async (quoteId) => {
  const { data } = await pharmaApi.post(`/pdv/settings/format-quote-message`, { quoteId });
  return data;
};

export const formatSaleMessage = async (orderId) => {
  const { data } = await pharmaApi.post(`/pdv/settings/format-sale-message`, { orderId });
  return data;
};

export const generateCredentials = async () => {
  const { data } = await pharmaApi.post("/pdv/settings/generate-credentials");
  return data;
};

export const getSettingsWithCredentialsInfo = async () => {
  const { data } = await pharmaApi.get("/pdv/settings/credentials-info");
  return data;
};

// ============================================
// Dashboard API
// ============================================

export const getDashboardSummary = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/dashboard/summary", { params: options });
  return data;
};

export const getOrdersDashboard = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/orders/dashboard", { params: options });
  return data;
};

// ============================================
// Reports API
// ============================================

export const getSalesReport = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/reports/sales", { params: options });
  return data;
};

export const getSalesAttendanceReport = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/reports/sales-attendance", { params: options });
  return data;
};

export const exportSalesReport = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/reports/sales-attendance/export", {
    params: options,
    responseType: "blob",
  });
  return data;
};

// ============================================
// Products Admin API
// ============================================

export const listProducts = async (options = {}) => {
  const { data } = await pharmaApi.get("/pdv/products", { params: options });
  // API returns { data: [...], total, page, ... } - adapt to expected format
  return {
    products: data.data || data,
    count: data.total || (data.data ? data.data.length : 0),
    hasMore: data.hasMore || false,
  };
};

export const createProduct = async (productData) => {
  const { data } = await pharmaApi.post("/pdv/products", productData);
  return data;
};

export const updateProduct = async (id, productData) => {
  const { data } = await pharmaApi.patch(`/pdv/products/${id}`, productData);
  return data;
};

export const deleteProduct = async (id) => {
  const { data } = await pharmaApi.delete(`/pdv/products/${id}`);
  return data;
};

// ============================================
// Product Import API
// ============================================

/**
 * Preview a CSV/TXT file for product import
 * @param {Object} options
 * @param {string} options.fileContent - Base64 encoded file content
 * @param {string} options.delimiter - Column delimiter (;, ,, 	, |)
 * @param {boolean} options.hasHeader - First row is header
 * @param {string} options.encoding - File encoding (utf-8, latin1, cp1252)
 * @returns {Promise<{columns: string[], preview: Object[], totalRows: number}>}
 */
export const previewProductImport = async ({ fileContent, delimiter, hasHeader, encoding }) => {
  const { data } = await pharmaApi.post("/pdv/products/import/preview", {
    fileContent,
    delimiter,
    hasHeader,
    encoding,
  });
  return data;
};

/**
 * Import products from CSV/TXT file (synchronous - for small files)
 * @param {Object} options
 * @param {string} options.fileContent - Base64 encoded file content
 * @param {Object} options.columnMapping - Mapping of product fields to column names
 * @param {Object} options.options - Import options (delimiter, hasHeader, encoding, updateExisting, storeId)
 * @returns {Promise<{total: number, created: number, updated: number, failed: number, errors: Array}>}
 */
export const importProducts = async ({ fileContent, columnMapping, options }) => {
  const { data } = await pharmaApi.post("/pdv/products/import", {
    fileContent,
    columnMapping,
    options,
  });
  return data;
};

/**
 * Start async import job with progress tracking (for large files)
 * @param {Object} options
 * @param {string} options.fileContent - Base64 encoded file content
 * @param {Object} options.columnMapping - Mapping of product fields to column names
 * @param {Object} options.options - Import options (delimiter, hasHeader, encoding, updateExisting, storeId)
 * @param {number} options.batchSize - Batch size for processing (default: 100)
 * @returns {Promise<{jobId: string, status: string, message: string, totalRows: number}>}
 */
export const startImportJob = async ({ fileContent, columnMapping, options, batchSize = 100 }) => {
  const { data } = await pharmaApi.post("/pdv/products/import/start", {
    fileContent,
    columnMapping,
    options,
    batchSize,
  });
  return data;
};

/**
 * Get import job status (for polling progress)
 * @param {string} jobId - Import job ID
 * @returns {Promise<ImportJobStatus>}
 */
export const getImportJobStatus = async (jobId) => {
  const { data } = await pharmaApi.get(`/pdv/products/import/jobs/${jobId}`);
  return data;
};

/**
 * List all import jobs
 * @returns {Promise<ImportJobStatus[]>}
 */
export const listImportJobs = async () => {
  const { data } = await pharmaApi.get("/pdv/products/import/jobs");
  return data;
};

/**
 * Pause a running import job
 * @param {string} jobId - Import job ID
 * @returns {Promise<ImportJobStatus>}
 */
export const pauseImportJob = async (jobId) => {
  const { data } = await pharmaApi.post(`/pdv/products/import/jobs/${jobId}/pause`);
  return data;
};

/**
 * Resume a paused or failed import job
 * @param {string} jobId - Import job ID
 * @param {Object} options
 * @param {number} options.startFromRow - Row to start from (optional)
 * @param {number} options.batchSize - New batch size (optional)
 * @returns {Promise<ImportJobStatus>}
 */
export const resumeImportJob = async (jobId, options = {}) => {
  const { data } = await pharmaApi.post(`/pdv/products/import/jobs/${jobId}/resume`, options);
  return data;
};

/**
 * Cancel an import job
 * @param {string} jobId - Import job ID
 * @returns {Promise<ImportJobStatus>}
 */
export const cancelImportJob = async (jobId) => {
  const { data } = await pharmaApi.post(`/pdv/products/import/jobs/${jobId}/cancel`);
  return data;
};

// ============================================
// Product Image API
// ============================================

/**
 * Upload an image for a product (base64)
 * @param {string} productId - Product ID
 * @param {string} imageData - Base64 encoded image data
 * @param {string} filename - Optional filename
 * @returns {Promise<{id: string, barcode: string, name: string, imageUrl: string, updated: boolean}>}
 */
export const uploadProductImage = async (productId, imageData, filename) => {
  const { data } = await pharmaApi.post(`/pdv/products/${productId}/image`, {
    imageData,
    filename,
  });
  return data;
};

/**
 * Set a product image URL directly
 * @param {string} productId - Product ID
 * @param {string} imageUrl - URL of the image
 * @returns {Promise<{id: string, barcode: string, name: string, imageUrl: string, updated: boolean}>}
 */
export const setProductImageUrl = async (productId, imageUrl) => {
  const { data } = await pharmaApi.put(`/pdv/products/${productId}/image-url`, {
    imageUrl,
  });
  return data;
};

/**
 * Delete a product image
 * @param {string} productId - Product ID
 * @returns {Promise<{id: string, barcode: string, name: string, imageUrl: null, updated: boolean}>}
 */
export const deleteProductImage = async (productId) => {
  const { data } = await pharmaApi.delete(`/pdv/products/${productId}/image`);
  return data;
};

/**
 * Search for external images (Open Food Facts, Cosmos, etc)
 * @param {string} productId - Product ID
 * @param {Object} options - Search options
 * @param {string} options.ean - EAN/Barcode to search
 * @param {string} options.productName - Product name to search
 * @returns {Promise<Array<{source: string, imageUrl: string, productName: string, thumbnailUrl?: string}>>}
 */
export const searchExternalImages = async (productId, { ean, productName } = {}) => {
  const { data } = await pharmaApi.post(`/pdv/products/${productId}/search-external-image`, {
    ean,
    productName,
  });
  return data;
};

// ============================================
// Product Enrichment API (Bluesoft Cosmos)
// ============================================

/**
 * Get enrichment statistics
 * @returns {Promise<{totalProducts: number, enrichedProducts: number, needingEnrichment: number, enrichmentPercentage: number, todayUsage: number, todayRemaining: number, dailyLimit: number}>}
 */
export const getEnrichmentStats = async () => {
  const { data } = await pharmaApi.get("/pdv/products/enrichment/stats");
  return data;
};

/**
 * Get products that need enrichment
 * @param {number} limit - Max products to return
 * @returns {Promise<Array<{id: string, barcode: string, name: string, manufacturer: string, description: string}>>}
 */
export const getProductsNeedingEnrichment = async (limit = 25) => {
  const { data } = await pharmaApi.get("/pdv/products/enrichment/pending", {
    params: { limit },
  });
  return data;
};

/**
 * Enrich a single product with Cosmos data
 * @param {string} productId - Product ID to enrich
 * @returns {Promise<{success: boolean, error?: string, data?: Object, remainingToday: number}>}
 */
export const enrichProduct = async (productId) => {
  const { data } = await pharmaApi.post(`/pdv/products/${productId}/enrich`);
  return data;
};

/**
 * Run automatic enrichment for pending products
 * @returns {Promise<{processed: number, enriched: number, failed: number, remainingToday: number, error?: string}>}
 */
export const runEnrichment = async () => {
  const { data } = await pharmaApi.post("/pdv/products/enrichment/run");
  return data;
};

/**
 * Get enrichment status statistics (by status type)
 * @returns {Promise<{pending: number, success: number, notFound: number, error: number, noBarcode: number, total: number}>}
 */
export const getEnrichmentStatusStats = async () => {
  const { data } = await pharmaApi.get("/pdv/products/enrichment/status-stats");
  return data;
};

/**
 * Get products filtered by enrichment status
 * @param {Object} options
 * @param {string} options.status - Status filter: pending, success, not_found, error, no_barcode, all
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 50)
 * @returns {Promise<{products: Array, total: number, stats: Object}>}
 */
export const getProductsByEnrichmentStatus = async ({ status = 'all', page = 1, limit = 50 } = {}) => {
  const { data } = await pharmaApi.get("/pdv/products/enrichment/by-status", {
    params: { status, page, limit },
  });
  return data;
};

export default pharmaApi;
